import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronLeft, Star, Quote, X } from 'lucide-react';
import clientReviewsData from '../ReviewsData.json';

const ClientReviews = () => {
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const reviewsRef = useRef(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);


  useEffect(() => {
    // Add fade-in animation when component mounts
    const loadingTimer = setTimeout(() => {
      setReviewsData(clientReviewsData);
      setLoading(false);
    }, 500);

    return () => clearTimeout(loadingTimer);
  }, []);

  // Auto-scroll through reviews every 5 seconds
  useEffect(() => {
    if (!loading && !isAnimating) {
      const interval = setInterval(() => {
        handleNext();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [loading, activeIndex, isAnimating]);

  const handleNext = () => {
    if (isAnimating || !reviewsData) return;
    setIsAnimating(true);
    setActiveIndex((prev) => (prev === reviewsData.reviews.length - 4 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrev = () => {
    if (isAnimating || !reviewsData) return;
    setIsAnimating(true);
    setActiveIndex((prev) => (prev === 0 ? reviewsData.reviews.length - 4 : prev - 1));
    setTimeout(() => setIsAnimating(false), 500);
  };

  const RatingStars = ({ rating }) => {
    return (
      <div className="flex animate-pulse">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={18}
            className={`${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} transition-all duration-300`}
          />
        ))}
      </div>
    );
  };

// Google logo component with animation
// const GoogleLogo = () => (
//     <div className="absolute top-4 right-4 transform transition-all duration-300 hover:scale-110">
//       <img
//         src="/icons/google.svg" // Path relative to the public folder
//         alt="Google"
//         className="h-5 object-contain"
//       />
//     </div>
//   );
// Zillow logo component with animation
const ZillowLogo = () => (
  <div className="absolute top-4 right-4 transform transition-all duration-300 hover:scale-110">
    <img
      src="https://cdn.brandfetch.io/id4MFt6BHH/theme/dark/logo.svg?c=1dxbfHSJFAPEGdCLU4o5B"
      alt="Zillow"
      className="h-5 object-contain"
    />
  </div>
);



  const FullReviewModal = ({ review, onClose }) => {
    if (!review) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full mx-4 relative shadow-2xl transform transition-all duration-300 ease-in-out scale-100 opacity-100">
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-gray-600 hover:text-red-700 transition-colors"
          >
            <X size={24} />
          </button>
          
          <div className="p-8">
            <div className="flex items-center mb-6">
              <div className={`w-16 h-16 overflow-hidden rounded-full mr-6 bg-gray-200 flex items-center justify-center`}>
                {!review.profileImg ? (
                  <span className="text-3xl font-bold text-red-700">{review.name.charAt(0)}</span>
                ) : (
                  <img
                    src={review.profileImg}
                    alt={review.name}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{review.name}</h2>
                <div className="flex items-center text-gray-500">
                  <span>{review.timeAgo}</span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <RatingStars rating={review.rating} />
            </div>

            <div className="text-lg text-gray-800 leading-relaxed mb-6">
              <Quote className="text-red-700 opacity-25 absolute -top-3 -left-3" size={48} />
              {review.fullText}
            </div>

            {review.fullerReview && (
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-xl font-semibold mb-4">Full Detailed Review</h3>
                <p className="text-gray-700 leading-relaxed">{review.fullerReview}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  


  const ReviewCard = ({ review, index }) => {
    const isHovered = hoveredCard === review.id;
    
    return (
      <div 
        className={`bg-white p-6 rounded-lg flex flex-col h-full shadow-md transition-all duration-500 relative
          ${isHovered ? 'transform scale-105 shadow-xl' : ''}
          ${isAnimating ? 'opacity-0' : 'opacity-100'}
        `}
        style={{
          animationDelay: `${index * 0.15}s`,
          transitionDelay: `${index * 0.05}s`
        }}
        onMouseEnter={() => setHoveredCard(review.id)}
        onMouseLeave={() => setHoveredCard(null)}
      >
        {/* Google logo positioned in the top right */}
        <ZillowLogo />
        
        <div className="relative mb-4">
          <Quote className="text-red-700 opacity-25 absolute -top-3 -left-3" size={32} />
          <RatingStars rating={review.rating} />
        </div>
        
        <p className="my-2 line-clamp-3 text-gray-700 italic">"{review.text}"</p>
        
        <button 
          className="text-red-700 mt-auto hover:underline transition-colors text-sm font-medium flex items-center"
          onClick={() => setSelectedReview(review)}
        >
          Read more
          <ChevronRight size={16} className="ml-1" />
        </button>
        
        <div className="flex items-center mt-4 border-t pt-4 border-gray-100">
          <div className={`w-10 h-10 overflow-hidden rounded-full mr-3 bg-gray-200 flex items-center justify-center transform transition-transform duration-500 ${isHovered ? 'scale-110' : ''}`}>
            {!review.profileImg ? (
              <span className="text-xl font-bold text-red-700">{review.name.charAt(0)}</span>
            ) : (
              <img
                src={review.profileImg}
                alt={review.name}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div>
            <p className="font-medium">{review.name}</p>
            <div className="flex items-center text-sm text-gray-500">
              <span>{review.timeAgo}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const LoadingAnimation = () => (
    <div className="flex justify-center items-center py-20">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-700"></div>
    </div>
  );

  if (loading) {
    return <LoadingAnimation />;
  }

  if (!reviewsData) {
    return (
      <div className="text-center py-10 animate-pulse">
        No reviews available at this time
      </div>
    );
  }

  const { agentInfo, ratings, reviews } = reviewsData;
  const visibleReviews = showAllReviews 
    ? reviews 
    : reviews.slice(activeIndex, activeIndex + 4);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 font-sans mt-10">
      <div 
        className="text-center mb-12 opacity-0 animate-fadeIn" 
        style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
      >
        <div className="flex justify-center mb-4">
          <div className="text-red-600 text-3xl animate-pulse">—★—</div>
        </div>
        <h1 className="text-4xl font-bold mb-4 relative">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-700 to-red-500">
            What my clients love about me
          </span>
        </h1>
        <p className="text-lg max-w-2xl mx-auto">
          After {agentInfo.experience} working in {agentInfo.location}, {agentInfo.name} is {agentInfo.description}
        </p>
      </div>

      <div 
        className="bg-gray-50 rounded-lg p-6 mb-8 shadow-lg transform transition-all duration-500 hover:shadow-xl opacity-0 animate-fadeIn"
        style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
      >
        {/* <div className="flex flex-wrap items-center border-b border-gray-200 pb-4 mb-4">
          <div className="mr-6 pb-2 flex items-center transition-all duration-300 hover:transform hover:scale-105">
            <img
            src="/icons/google.svg" // Path relative to the public folder

              alt="Google"
              className="mr-1 h-5 object-contain"
            />
            <span className="ml-2 font-bold">{ratings.platforms.google}</span>
          </div>
          {Object.entries(ratings.platforms).filter(([platform]) => platform !== 'google').map(([platform, rating], index) => (
            <div 
              key={platform} 
              className="mr-6 pb-2 flex items-center transition-all duration-300 hover:transform hover:scale-105"
              style={{ animationDelay: `${index * 0.15 + 0.6}s` }}
            >
              <img
                src="/api/placeholder/20/20"
                alt={platform}
                className="mr-1 w-5 h-5"
              />
              <span className="capitalize">{platform}</span> 
              <span className="ml-2 font-bold">{rating}</span>
            </div>
          ))}
        </div> */}

        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center">
            <div>
              <div className="text-2xl font-bold">Overall Rating</div>
              <div className="flex items-center">
                <span className="text-4xl font-bold mr-2 text-red-700">{ratings.overall}</span>
                <RatingStars rating={Math.round(ratings.overall)} />
                <span className="ml-2 text-gray-500">({ratings.totalReviews})</span>
              </div>
            </div>
          </div>
          {/* <button 
            className="bg-red-700 text-white py-2 px-6 rounded hover:bg-red-800 transition-colors transform hover:scale-105 duration-300 shadow-md flex items-center"
            onClick={() => alert('Write a review')}
          >
            <Star size={16} className="mr-2" />
            Write a review
          </button> */}
        </div>
      </div>

      <div className="relative mt-16">
        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-500"
          ref={reviewsRef}
          style={{ 
            opacity: isAnimating ? 0.5 : 1,
            transform: isAnimating ? 'translateX(-10px)' : 'translateX(0)'
          }}
        >
          {visibleReviews.map((review, idx) => (
            <ReviewCard key={review.id} review={review} index={idx} />
          ))}
        </div>
        
        {!showAllReviews && (
          <>
            <button 
              className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-3 shadow-lg opacity-75 hover:opacity-100 transition-all duration-300 hover:bg-red-700 hover:text-white z-10"
              onClick={handlePrev}
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white rounded-full p-3 shadow-lg opacity-75 hover:opacity-100 transition-all duration-300 hover:bg-red-700 hover:text-white z-10"
              onClick={handleNext}
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}


      {selectedReview && (
        <FullReviewModal 
          review={selectedReview} 
          onClose={() => setSelectedReview(null)} 
        />
      )}
      </div>
      
      <div className="mt-8 text-center">
        <button
          className="bg-transparent border border-red-700 text-red-700 py-2 px-6 rounded hover:bg-red-700 hover:text-white transition-all duration-300"
          onClick={() => setShowAllReviews(!showAllReviews)}
        >
          {showAllReviews ? 'Show Carousel View' : 'View All Reviews'}
        </button>
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ClientReviews;